import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Typography,
} from '@eye-able/ui';
import { IconArrowLeft, IconListTree } from '@tabler/icons-react';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type {
  PolicyAssetType,
  PolicyMatchMode,
  PolicyPriority,
  PolicyRuleCondition,
  PolicyViolationLabel,
} from '@http/queries/useCreatePolicy';
import { getRuleFieldLabel, getRuleOperatorLabel } from './PolicyRuleLabels';
import { useGovernanceAccess } from '@/hooks/useGovernanceAccess';
import { ReadOnlyBanner } from '@/components/access/ReadOnlyBanner';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';

/** A single ready-made policy template; passed to the new-policy form to prefill it. */
export interface PolicyTemplate {
  nameKey: string;
  summaryKey: string;
  assetType: PolicyAssetType;
  label: PolicyViolationLabel;
  priority: PolicyPriority;
  matchMode: PolicyMatchMode;
  conditions: PolicyRuleCondition[];
}

const policyTemplates = [
  {
    nameKey: 'templateBrandSafePageMetadata',
    assetType: 'html-pages',
    label: 'required',
    priority: 'high',
    summaryKey: 'templateBrandSafePageMetadataSummary',
    matchMode: 'all',
    conditions: [
      {
        field: 'h1-text',
        operator: 'contains-words',
        value: 'Eye-Able',
      },
      {
        field: 'page-title-length',
        operator: 'greater-than',
        value: '29',
      },
      {
        field: 'page-title-length',
        operator: 'less-than',
        value: '61',
      },
    ],
  },
  // The "No external partner domains" template is parked with the link
  // fields (external-link-count is not offered in the builder for now).
  {
    nameKey: 'templateGermanOnlyLegalPages',
    assetType: 'html-pages',
    label: 'matches',
    priority: 'medium',
    summaryKey: 'templateGermanOnlyLegalPagesSummary',
    matchMode: 'all',
    conditions: [
      {
        field: 'visible-text',
        operator: 'contains',
        value: 'Impressum',
      },
      {
        field: 'visible-text',
        operator: 'contains',
        value: 'Datenschutz',
      },
    ],
  },
  {
    nameKey: 'templatePdfSizeGuardrail',
    assetType: 'documents',
    label: 'required',
    priority: 'medium',
    summaryKey: 'templatePdfSizeGuardrailSummary',
    matchMode: 'all',
    conditions: [
      {
        field: 'file-size',
        operator: 'less-than',
        value: '10 MB',
      },
    ],
  },
] satisfies PolicyTemplate[];

function ConditionsPreviewDialog({
  title,
  matchMode,
  conditions,
}: {
  title: string;
  matchMode: PolicyMatchMode;
  conditions: PolicyRuleCondition[];
}) {
  const { t } = useInlineTranslation();
  const [open, setOpen] = useState(false);

  const matchModeLabel =
    matchMode === 'all'
      ? t('allConditionsMustMatch')
      : t('atLeastOneConditionMustMatch');

  return (
    <>
      <Button variant='outline' size='sm' onClick={() => setOpen(true)}>
        <IconListTree aria-hidden />
        {t('previewConditions')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent maxWidth='md' className='eye:w-full'>
          <DialogTitle>{t('previewConditions')}</DialogTitle>
          <DialogDescription>{title}</DialogDescription>

          <div className='eye:flex eye:flex-col eye:gap-xs eye:pt-xs'>
            {conditions.length > 1 ? (
              <div className='eye:flex eye:flex-col eye:gap-5xs'>
                <Typography variant='body-m-highlight'>
                  {matchModeLabel}
                </Typography>
              </div>
            ) : null}

            {conditions.map((condition, index) => (
              <div
                key={`${title}-${condition.field}-${index}`}
                className='eye:grid eye:grid-cols-1 eye:gap-xs eye:rounded-md eye:border eye:border-grey-20 eye:p-s eye:md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] eye:md:items-center'
              >
                <Typography>{t(getRuleFieldLabel(condition.field))}</Typography>
                <Typography className='eye:text-grey-120'>
                  {t(getRuleOperatorLabel(condition.operator))}
                </Typography>
                <Typography variant='body-m-highlight'>
                  {condition.value}
                </Typography>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PolicyTemplateLibraryContent() {
  const { t } = useInlineTranslation();
  const navigate = useNavigate();
  const { canEdit } = useGovernanceAccess('customPolicies');

  return (
    <div className='eye:flex eye:flex-col eye:gap-m'>
      <section className='eye:flex eye:flex-col eye:gap-s'>
        <Button asChild variant='ghost' size='sm' className='eye:self-start'>
          <RouterLink to='/policies'>
            <IconArrowLeft aria-hidden />
            {t('backToPolicies')}
          </RouterLink>
        </Button>

        <div className='eye:flex eye:max-w-[920px] eye:flex-col eye:gap-3xs'>
          <Typography tag='h1' variant='headline-xl'>
            {t('templateLibrary')}
          </Typography>
          <Typography className='eye:text-grey-120'>
            {t('templateLibraryDescription')}
          </Typography>
        </div>
      </section>

      <ReadOnlyBanner show={!canEdit} />

      <section className='eye:grid eye:gap-s'>
        {policyTemplates.map((template) => (
          <Card key={template.nameKey} className='eye:h-full'>
            <CardContent className='eye:flex eye:h-full eye:flex-col eye:gap-s eye:p-s'>
              <div className='eye:flex eye:flex-col eye:gap-4xs'>
                <Typography tag='h2' variant='headline-s'>
                  {t(template.nameKey)}
                </Typography>
                <Typography className='eye:text-grey-120'>
                  {t(template.summaryKey)}
                </Typography>
              </div>

              <div className='eye:grid eye:grid-cols-1 eye:gap-xs eye:text-14 eye:text-grey-120 eye:md:grid-cols-2'>
                <div className='eye:flex eye:flex-col eye:gap-5xs'>
                  <Typography
                    variant='body-s-highlight'
                    className='eye:text-grey-120'
                  >
                    {t('label')}
                  </Typography>
                  <Typography>
                    {t(`violationLabel${template.label}`)}
                  </Typography>
                </div>
                <div className='eye:flex eye:flex-col eye:gap-5xs'>
                  <Typography
                    variant='body-s-highlight'
                    className='eye:text-grey-120'
                  >
                    {t('priority')}
                  </Typography>
                  <Typography>{t(`priority${template.priority}`)}</Typography>
                </div>
                <div className='eye:flex eye:flex-col eye:gap-5xs'>
                  <Typography
                    variant='body-s-highlight'
                    className='eye:text-grey-120'
                  >
                    {t('type')}
                  </Typography>
                  <Typography>{t(`assetType${template.assetType}`)}</Typography>
                </div>
              </div>

              <div className='eye:mt-auto eye:flex eye:justify-between eye:gap-s'>
                <ConditionsPreviewDialog
                  title={t(template.nameKey)}
                  matchMode={template.matchMode}
                  conditions={template.conditions}
                />
                {canEdit ? (
                  <Button
                    onClick={() =>
                      navigate('/policies/new', { state: { template } })
                    }
                  >
                    {t('useTemplate')}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
}

function PolicyTemplateLibrary() {
  return (
    <GovernanceAccessBoundary area='customPolicies'>
      <PolicyTemplateLibraryContent />
    </GovernanceAccessBoundary>
  );
}

export default PolicyTemplateLibrary;
